import React, { useState } from 'react';
import api from '../../services/api';

const EmailCaptureForm = ({ source = 'landing', className = '' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');
  const [position, setPosition] = useState(null);
  const [referralUrl, setReferralUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    try {
      const res = await api.post('/api/waitlist', { email: email.trim(), source });
      setStatus('success');
      setPosition(res.data.position);
      if (res.data.referralCode) {
        setReferralUrl(`${window.location.origin}/waitlist.html?ref=${res.data.referralCode}`);
      }
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.error || 'Something went wrong. Please try again.');
    }
  };

  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — the link is visible to copy manually
    }
  };

  if (status === 'success') {
    return (
      <div className={`text-center ${className}`}>
        <div className="inline-flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-medium">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          You're on the waitlist{position ? ` — #${position}` : ''}!
        </div>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          We'll email you when your spot opens.
        </p>
        {referralUrl && (
          <div className="mt-4 inline-flex flex-col items-center gap-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 px-4 py-3">
            <p className="text-xs text-slate-600 dark:text-slate-300">
              Move up 3 spots for each person who joins with your link:
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-blue-700 dark:text-blue-300 break-all">{referralUrl}</span>
              <button
                type="button"
                onClick={copyReferral}
                className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`${className}`}>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
          placeholder="Enter your email"
          required
          className="flex-1 px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {status === 'loading' ? 'Joining...' : 'Join the Waitlist'}
        </button>
      </div>
      {status === 'error' && (
        <p className="mt-2 text-sm text-red-500">{message}</p>
      )}
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        We'll only email you about your spot and early access. Unsubscribe anytime.{' '}
        <a href="/privacy-policy.html" className="underline hover:text-slate-700 dark:hover:text-slate-200">Privacy policy</a>
      </p>
    </form>
  );
};

export default EmailCaptureForm;
